import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import { Layout } from '../../components/layout/Layout';
import ExportButton from '../../components/ExportButton';
import { Checkbox } from '../../components/ui/checkbox';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import { 
  Monitor, Plus, Search, Eye, Edit, Trash2, Copy, X,
  Building2, MapPin, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown,
  CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';

const Monitores = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions('micro', 'monitores');
  
  // Data states
  const [monitores, setMonitores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ 
    total: 0, 
    active: 0, 
    inactive: 0, 
    sin_estado: 0,
    by_brand: [],
    by_size: []
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  
  // Filter states
  const [filters, setFilters] = useState({ search: '', brand_id: '', status_id: '', site_id: '', cliente_id: '', asset_tag_filter: '', modelo_filter: '', size_filter: '', usuario_filter: '', legajo_filter: '', location_filter: '' });
  const [filterOptions, setFilterOptions] = useState({ brands: [], statuses: [], sites: [], sizes: [] });
  const [formOptions, setFormOptions] = useState({ empresas: [], sites: [], brands: [], statuses: [], ubicaciones: [], proveedores: [], usuarios: [], clientes: [] });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Bulk selection
  const { 
    selectedIds, selectedCount, isAllSelected, isSomeSelected, 
    isSelected, toggleRow, toggleAll, clearSelection 
  } = useBulkSelection(monitores);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedMonitor, setSelectedMonitor] = useState(null);
  const [formData, setFormData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [monitorToDelete, setMonitorToDelete] = useState(null);

  // Fetch functions
  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/monitores/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/monitores/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/monitores/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const fetchMonitores = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      
      if (filters.search) params.append('search', filters.search);
      if (filters.brand_id) params.append('brand_id', filters.brand_id);
      if (filters.status_id) params.append('status_id', filters.status_id);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);
      if (filters.asset_tag_filter) params.append('asset_tag_filter', filters.asset_tag_filter);
      if (filters.modelo_filter) params.append('modelo_filter', filters.modelo_filter);
      if (filters.size_filter) params.append('size_filter', filters.size_filter);
      if (filters.usuario_filter) params.append('usuario_filter', filters.usuario_filter);
      if (filters.legajo_filter) params.append('legajo_filter', filters.legajo_filter);
      if (filters.location_filter) params.append('location_filter', filters.location_filter);
      
      const response = await api.get(`/assets/monitores?${params.toString()}`);
      setMonitores(response.data.data);
      setPagination(prev => ({ 
        ...prev, 
        total: response.data.total, 
        pages: response.data.pages 
      }));
    } catch (error) {
      console.error('Error fetching monitores:', error);
      toast.error('Error al cargar monitores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
    fetchFormOptions();
  }, []);

  useEffect(() => {
    fetchMonitores();
  }, [pagination.page, pagination.limit]);

  // Debounced filter fetch (800ms)
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchMonitores();
    }, 800);
    return () => clearTimeout(t);
  }, [filters]);

  // Handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ search: '', brand_id: '', status_id: '', site_id: '', cliente_id: '', asset_tag_filter: '', modelo_filter: '', size_filter: '', usuario_filter: '', legajo_filter: '', location_filter: '' });
  };

  const hasActiveFilters = Object.values(filters).some(v => v && v !== '');

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({});
    setShowModal(true);
  };

  const openEditModal = (monitor) => {
    setModalMode('edit');
    setSelectedMonitor(monitor);
    setFormData({
      empresa_id: monitor.empresa_id || '',
      site_id: monitor.site_id || '',
      brand_id: monitor.brand_id || '',
      status_id: monitor.status_id || '',
      asset_tag: monitor.asset_tag || '',
      serial_number: monitor.serial_number || '',
      modelo: monitor.modelo || '',
      size_inches: monitor.size_inches || '',
      ports: monitor.ports || '',
      location_id: monitor.location_id || '',
      supplier_id: monitor.supplier_id || '',
      purchase_date: monitor.purchase_date || '',
      assigned_user_id: monitor.assigned_user_id || '',
      cliente_id: monitor.cliente_id || '',
      comments: monitor.comments || ''
    });
    setShowModal(true);
  };

  const openCopyModal = (monitor) => {
    setModalMode('create');
    setFormData({
      empresa_id: monitor.empresa_id || '',
      site_id: monitor.site_id || '',
      brand_id: monitor.brand_id || '',
      status_id: monitor.status_id || '',
      modelo: monitor.modelo || '',
      size_inches: monitor.size_inches || '',
      ports: monitor.ports || '',
      location_id: monitor.location_id || '',
      supplier_id: monitor.supplier_id || ''
    });
    setShowModal(true);
    toast.info('Datos copiados - Complete los campos unicos');
  };

  const handleSubmit = async () => {
    try {
      // Clean form data - convert empty strings to null for IDs
      const cleanData = { ...formData };
      ['assigned_user_id', 'empresa_id', 'site_id', 'brand_id', 'status_id', 'location_id', 'supplier_id', 'cliente_id'].forEach(field => {
        if (cleanData[field] === '' || cleanData[field] === 'none') {
          cleanData[field] = null;
        } else if (cleanData[field]) {
          cleanData[field] = parseInt(cleanData[field], 10) || null;
        }
      });
      
      // Handle size_inches as decimal
      if (cleanData.size_inches === '' || cleanData.size_inches === null) {
        cleanData.size_inches = null;
      } else {
        cleanData.size_inches = parseFloat(cleanData.size_inches) || null;
      }
      
      if (modalMode === 'create') {
        await api.post('/assets/monitores', cleanData);
        toast.success('Monitor creado exitosamente');
      } else {
        await api.put(`/assets/monitores/${selectedMonitor.id}`, cleanData);
        toast.success('Monitor actualizado exitosamente');
      }
      setShowModal(false);
      fetchMonitores();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/monitores/${monitorToDelete.id}`);
      toast.success('Monitor eliminado exitosamente');
      setShowDeleteModal(false);
      setMonitorToDelete(null);
      fetchMonitores();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedMonitores = [...monitores].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = a[sortConfig.key] || '';
    const bVal = b[sortConfig.key] || '';
    const comparison = String(aVal).localeCompare(String(bVal));
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return <ArrowUpDown className="h-3 w-3 opacity-30" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3 w-3" /> 
      : <ArrowDown className="h-3 w-3" />;
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="badge badge-default">Sin estado</span>;
    if (status.toLowerCase().includes('activo')) return <span className="badge badge-green">{status}</span>;
    if (status.toLowerCase().includes('inactivo')) return <span className="badge badge-red">{status}</span>;
    return <span className="badge badge-default">{status}</span>;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Monitor className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-2xl font-semibold">Monitores</h1>
              <p className="text-sm text-muted-foreground">Gestion de monitores</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/monitores"
              filters={filters}
              filename="monitores"
            />
            {canCreate && (
              <Button onClick={openCreateModal} data-testid="new-monitor-btn">
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Nuevo Monitor
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards - Horizontal Compacto */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="monitores-stats">
          <div className="stats-card flex items-center gap-3" data-testid="stats-total">
            <Monitor className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-activos">
            <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.active}</div>
              <div className="stats-card-label">Activos</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-inactivos">
            <XCircle className="h-6 w-6 text-red-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-red-500">{stats.inactive}</div>
              <div className="stats-card-label">Inactivos</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-sin-estado">
            <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-yellow-500">{stats.sin_estado}</div>
              <div className="stats-card-label">Sin Estado</div>
            </div>
          </div>
        </div>

        {/* Global Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  placeholder="Buscar en todos los campos..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                  data-testid="global-search-input"
                />
              </div>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="h-4 w-4 mr-1" strokeWidth={1.5} />
                  Limpiar filtros
                </Button>
              )}
              
              <div className="w-[150px]">
                <Label className="text-xs">Marca</Label>
                <Select value={filters.brand_id} onValueChange={(v) => handleFilterChange('brand_id', v)}>
                  <SelectTrigger data-testid="filter-brand">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {filterOptions.brands?.map(b => (
                      <SelectItem key={b.id} value={String(b.id)}>{b.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Edit Bar */}
        {selectedCount > 0 && (
          <BulkEditBar
            onClear={clearSelection}
            endpoint="/assets/monitores"
            selectedIds={selectedIds}
            onSuccess={() => {
              fetchMonitores();
              fetchStats();
              clearSelection();
            }}
            fields={['site_id', 'empresa_id', 'status_id', 'location_id', 'assigned_user_id']}
            options={{
              sites: formOptions.sites,
              empresas: formOptions.empresas,
              statuses: formOptions.statuses,
              ubicaciones: formOptions.ubicaciones,
              usuarios: formOptions.usuarios
            }}
          />
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-3 w-10">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={toggleAll}
                        className={isSomeSelected ? 'opacity-50' : ''}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th 
                      className="text-left p-3 text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('asset_tag')}
                    >
                      <div className="flex items-center gap-1">
                        ASSET TAG
                        <SortIcon field="asset_tag" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('brand_name')}
                    >
                      <div className="flex items-center gap-1">
                        MARCA
                        <SortIcon field="brand_name" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('modelo')}
                    >
                      <div className="flex items-center gap-1">
                        MODELO
                        <SortIcon field="modelo" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/50 hidden md:table-cell"
                      onClick={() => handleSort('size_inches')}
                    >
                      <div className="flex items-center gap-1">
                        TAMANO
                        <SortIcon field="size_inches" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/50 hidden lg:table-cell"
                      onClick={() => handleSort('site_name')}
                    >
                      <div className="flex items-center gap-1">
                        SITE
                        <SortIcon field="site_name" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/50 hidden lg:table-cell"
                      onClick={() => handleSort('location_name')}
                    >
                      <div className="flex items-center gap-1">
                        UBICACIÓN
                        <SortIcon field="location_name" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/50 hidden lg:table-cell"
                      onClick={() => handleSort('assigned_user_name')}
                    >
                      <div className="flex items-center gap-1">
                        USUARIO
                        <SortIcon field="assigned_user_name" />
                      </div>
                    </th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground hidden lg:table-cell">LEGAJO</th>
                    <th 
                      className="text-left p-3 text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/50 hidden xl:table-cell"
                      onClick={() => handleSort('cliente_name')}
                    >
                      <div className="flex items-center gap-1">
                        CLIENTE
                        <SortIcon field="cliente_name" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('status_name')}
                    >
                      <div className="flex items-center gap-1">
                        ESTADO
                        <SortIcon field="status_name" />
                      </div>
                    </th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">ACCIONES</th>
                  </tr>
                  <tr className="border-b bg-muted/30">
                    <th className="p-2 w-10"></th>
                    <th className="p-2"><Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.asset_tag_filter} onChange={(e) => handleFilterChange('asset_tag_filter', e.target.value)} /></th>
                    <th className="p-2">
                      <Select value={filters.brand_id || undefined} onValueChange={(v) => handleFilterChange('brand_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent><SelectItem value="todos">Todos</SelectItem>{filterOptions.brands?.map(b => (<SelectItem key={b.id} value={String(b.id)}>{b.nombre}</SelectItem>))}</SelectContent>
                      </Select>
                    </th>
                    <th className="p-2"><Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.modelo_filter} onChange={(e) => handleFilterChange('modelo_filter', e.target.value)} /></th>
                    <th className="p-2 hidden md:table-cell"><Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.size_filter} onChange={(e) => handleFilterChange('size_filter', e.target.value)} /></th>
                    <th className="p-2 hidden lg:table-cell">
                      <Select value={filters.site_id || undefined} onValueChange={(v) => handleFilterChange('site_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent><SelectItem value="todos">Todos</SelectItem>{filterOptions.sites?.map(s => (<SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>))}</SelectContent>
                      </Select>
                    </th>
                    <th className="p-2 hidden lg:table-cell"><Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.location_filter} onChange={(e) => handleFilterChange('location_filter', e.target.value)} /></th>
                    <th className="p-2 hidden lg:table-cell"><Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.usuario_filter} onChange={(e) => handleFilterChange('usuario_filter', e.target.value)} /></th>
                    <th className="p-2 hidden lg:table-cell"><Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.legajo_filter} onChange={(e) => handleFilterChange('legajo_filter', e.target.value)} /></th>
                    <th className="p-2 hidden xl:table-cell">
                      <Select value={filters.cliente_id || undefined} onValueChange={(v) => handleFilterChange('cliente_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent><SelectItem value="todos">Todos</SelectItem>{formOptions.clientes?.map(c => (<SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>))}</SelectContent>
                      </Select>
                    </th>
                    <th className="p-2">
                      <Select value={filters.status_id || undefined} onValueChange={(v) => handleFilterChange('status_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent><SelectItem value="todos">Todos</SelectItem>{filterOptions.statuses?.map(s => (<SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>))}<SelectItem value="null">Sin estado</SelectItem></SelectContent>
                      </Select>
                    </th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="text-center p-8 text-muted-foreground">
                        Cargando...
                      </td>
                    </tr>
                  ) : sortedMonitores.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center p-8 text-muted-foreground">
                        No se encontraron monitores
                      </td>
                    </tr>
                  ) : (
                    sortedMonitores.map(monitor => (
                      <tr key={monitor.id} className="border-b hover:bg-muted/30">
                        <td className="p-3">
                          <Checkbox
                            checked={isSelected(monitor.id)}
                            onCheckedChange={() => toggleRow(monitor.id)}
                            data-testid={`select-row-${monitor.id}`}
                          />
                        </td>
                        <td className="p-3 font-medium">{monitor.asset_tag || '-'}</td>
                        <td className="p-3">{monitor.brand_name || '-'}</td>
                        <td className="p-3">{monitor.modelo || '-'}</td>
                        <td className="p-3 hidden md:table-cell">
                          {monitor.size_inches ? `${monitor.size_inches}"` : '-'}
                        </td>
                        <td className="p-3 hidden lg:table-cell">
                          {monitor.site_name && (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
                              {monitor.site_name}
                            </div>
                          )}
                        </td>
                        <td className="p-3 hidden lg:table-cell">{monitor.location_name || '-'}</td>
                        <td className="p-3 hidden lg:table-cell">{monitor.assigned_user_name || '-'}</td>
                        <td className="p-3 hidden lg:table-cell">{monitor.usuario_legajo || '-'}</td>
                        <td className="p-3 hidden xl:table-cell">{monitor.cliente_name || '-'}</td>
                        <td className="p-3">{getStatusBadge(monitor.status_name)}</td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => navigate(`/micro/monitores/${monitor.id}`)}
                              title="Ver detalle"
                              data-testid={`view-monitor-${monitor.id}`}
                            >
                              <Eye className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                            {canUpdate && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEditModal(monitor)}
                                title="Editar"
                                data-testid={`edit-monitor-${monitor.id}`}
                              >
                                <Edit className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canCreate && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openCopyModal(monitor)}
                                title="Copiar"
                                data-testid={`copy-monitor-${monitor.id}`}
                              >
                                <Copy className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => {
                                  setMonitorToDelete(monitor);
                                  setShowDeleteModal(true);
                                }}
                                title="Eliminar"
                                data-testid={`delete-monitor-${monitor.id}`}
                              >
                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t">
              <div className="text-sm text-muted-foreground">
                Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                </Button>
                <span className="text-sm">
                  Pagina {pagination.page} de {pagination.pages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.pages}
                >
                  <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {modalMode === 'create' ? 'Nuevo Monitor' : 'Editar Monitor'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-1">
                <Label className="text-xs">Asset Tag</Label>
                <Input
                  value={formData.asset_tag || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, asset_tag: e.target.value }))}
                  placeholder="MON-001"
                  data-testid="input-asset-tag"
                  data-validation={modalMode === 'create' && !formData.asset_tag ? 'required-empty' : ''}
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Numero de Serie</Label>
                <Input
                  value={formData.serial_number || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                  placeholder="SN123456"
                  data-testid="input-serial"
                  data-validation={modalMode === 'create' && !formData.serial_number ? 'required-empty' : ''}
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Marca</Label>
                <Select
                  value={formData.brand_id ? String(formData.brand_id) : "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-brand" data-validation={modalMode === 'create' && formData.brand_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar...</SelectItem>
                    {formOptions.brands?.map(b => (
                      <SelectItem key={b.id} value={String(b.id)}>{b.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Modelo</Label>
                <Input
                  value={formData.modelo || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))}
                  placeholder="P2419H"
                  data-testid="input-modelo"
                  data-validation={modalMode === 'create' && formData.modelo ? 'valid' : ''}
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Tamano (pulgadas)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.size_inches || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, size_inches: e.target.value }))}
                  placeholder="24"
                  data-testid="input-size"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Puertos</Label>
                <Input
                  value={formData.ports || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, ports: e.target.value }))}
                  placeholder="HDMI, DisplayPort, VGA"
                  data-testid="input-ports"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Estado</Label>
                <Select
                  value={formData.status_id ? String(formData.status_id) : "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-status" data-validation={modalMode === 'create' && formData.status_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar...</SelectItem>
                    {formOptions.statuses?.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Site</Label>
                <Select
                  value={formData.site_id ? String(formData.site_id) : "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-site" data-validation={modalMode === 'create' && formData.site_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar...</SelectItem>
                    {formOptions.sites?.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Empresa</Label>
                <Select
                  value={formData.empresa_id ? String(formData.empresa_id) : "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, empresa_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-empresa" data-validation={modalMode === 'create' && formData.empresa_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar...</SelectItem>
                    {formOptions.empresas?.map(e => (
                      <SelectItem key={e.id} value={String(e.id)}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Ubicacion</Label>
                <Select
                  value={formData.location_id ? String(formData.location_id) : "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-location" data-validation={modalMode === 'create' && formData.location_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar...</SelectItem>
                    {formOptions.ubicaciones?.map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Proveedor</Label>
                <Select
                  value={formData.supplier_id ? String(formData.supplier_id) : "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, supplier_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-supplier" data-validation={modalMode === 'create' && formData.supplier_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar...</SelectItem>
                    {formOptions.proveedores?.map(p => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Usuario</Label>
                <Select
                  value={formData.assigned_user_id ? String(formData.assigned_user_id) : "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, assigned_user_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-user">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin seleccionar</SelectItem>
                    {formOptions.usuarios?.map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Cliente</Label>
                <Select
                  value={formData.cliente_id ? String(formData.cliente_id) : "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, cliente_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-cliente">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin seleccionar</SelectItem>
                    {formOptions.clientes?.map(c => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Fecha de Compra</Label>
                <Input
                  type="date"
                  value={formData.purchase_date || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))}
                  data-testid="input-purchase-date"
                />
              </div>
              
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Comentarios</Label>
                <Input
                  value={formData.comments || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                  placeholder="Notas adicionales..."
                  data-testid="input-comments"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} data-testid="submit-form">
                {modalMode === 'create' ? 'Crear' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Eliminacion</DialogTitle>
            </DialogHeader>
            <p className="py-4">
              ¿Esta seguro de eliminar el monitor <strong>{monitorToDelete?.asset_tag || monitorToDelete?.serial_number || 'seleccionado'}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Monitores;
